import { ReactElement, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import EditIcon from '@mui/icons-material/Edit';

import Team from '../../types/Team';

import TeamListItem from './TeamListItem';

export interface TeamReviewAndFinalizeProps {
  team: Team;
  isEditing?: boolean;
  onEditingOpen?: () => void;
  /** Pending logo file for preview during create flow */
  pendingLogoFile?: File | null;
}

export default function TeamReviewAndFinalize(props: TeamReviewAndFinalizeProps): ReactElement {
  const {
    team,
    isEditing = false,
    onEditingOpen,
    pendingLogoFile,
  } = props;

  // Create a preview URL for the pending logo file
  const [previewLogoUrl, setPreviewLogoUrl] = useState<string | null>(null);

  useEffect(() => {
    if (pendingLogoFile) {
      const url = URL.createObjectURL(pendingLogoFile);
      setPreviewLogoUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewLogoUrl(null);
    }
  }, [pendingLogoFile]);

  const handleElementClick = (field: 'logo' | 'description') => {
    if (onEditingOpen) {
      onEditingOpen();
    }
    // Focus the field after a brief delay
    setTimeout(() => {
      const fieldElement = document.getElementById(field === 'logo' ? 'name' : field);
      if (fieldElement) {
        fieldElement.focus();
        fieldElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      <Stack spacing={0.5} sx={{
        alignItems: "flex-start"
      }}>
        <Typography variant="body1" sx={{
          color: "text.secondary"
        }}>
          This is how the team will appear in the Teams list.
        </Typography>
        {!isEditing && onEditingOpen && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon />}
            onClick={onEditingOpen}
          >
            Edit
          </Button>
        )}
      </Stack>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Paper variant="outlined" sx={{ maxWidth: '32rem', width: '100%' }}>
          <TeamListItem team={team} previewLogoUrl={previewLogoUrl} />
        </Paper>
      </Box>
      {/* "Add" links for missing optional elements */}
      {((!team.logo && !pendingLogoFile) || !team.description?.trim()) && (
        <Stack direction="row" spacing={2} sx={{
          justifyContent: "center"
        }}>
          {!team.logo && !pendingLogoFile && (
            <Link
              component="button"
              variant="body2"
              onClick={() => handleElementClick('logo')}
            >
              + Add logo
            </Link>
          )}
          {!team.description?.trim() && (
            <Link
              component="button"
              variant="body2"
              onClick={() => handleElementClick('description')}
            >
              + Add description
            </Link>
          )}
        </Stack>
      )}
    </Stack>
  );
}
